/**
 * The CMS body renderer for news, guides and FAQ answers.
 *
 * CMS bodies are written by admins in the panel's plain textarea, so they arrive
 * as text with blank-line paragraphs and the occasional list or link. Everything
 * is escaped FIRST and only then the handful of tags we understand are put back,
 * so a stray "<script>" in an article can never execute even though the author
 * is trusted.
 *
 * The grammar is deliberately small — headings alone in their block, bullet and
 * numbered lists, paragraphs, **bold** and [text](http… or /relative) links. The
 * seeded guides are tested against exactly this (backend seed-guides.test.ts),
 * so widening it is fine and narrowing it is not.
 */

export function esc(s: unknown): string {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function inline(s: string): string {
  return esc(s)
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+|\/[^\s)]*)\)/g, '<a href="$2">$1</a>');
}

export function markdown(src: string | null | undefined): string {
  const blocks = String(src || '').replace(/\r\n/g, '\n').split(/\n{2,}/);
  let out = '';
  for (const block of blocks) {
    const lines = block.split('\n').filter((l) => l.trim() !== '');
    if (!lines.length) continue;
    if (lines.length === 1 && /^###\s+/.test(lines[0])) { out += '<h3>' + inline(lines[0].replace(/^###\s+/, '')) + '</h3>'; continue; }
    if (lines.length === 1 && /^##?\s+/.test(lines[0])) { out += '<h2>' + inline(lines[0].replace(/^##?\s+/, '')) + '</h2>'; continue; }
    if (lines.every((l) => /^\s*[-*]\s+/.test(l))) {
      out += '<ul>' + lines.map((l) => '<li>' + inline(l.replace(/^\s*[-*]\s+/, '')) + '</li>').join('') + '</ul>';
      continue;
    }
    if (lines.every((l) => /^\s*\d+[.)]\s+/.test(l))) {
      out += '<ol>' + lines.map((l) => '<li>' + inline(l.replace(/^\s*\d+[.)]\s+/, '')) + '</li>').join('') + '</ol>';
      continue;
    }
    out += '<p>' + lines.map(inline).join('<br />') + '</p>';
  }
  return out;
}
